import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { ensureCoachUser } from '@4cm/core/ensureCoachUser';

/**
 * `profileStatus` tracks whether the signed-in auth user has a
 * complete `public.users` row (with DoB + role). v1 launch posture:
 * every new sign-in must run through the finish-signup form before
 * the rest of the app renders, so under-13 DoBs route to the
 * deferred screen instead of creating a row.
 *
 *   - 'loading'    initial / re-checking
 *   - 'missing'    auth session exists but no users row — show finish-signup
 *   - 'complete'   users row exists with DoB — proceed to app
 *
 * See docs/spec/22a-v1-launch-posture.md.
 */
export type ProfileStatus = 'loading' | 'missing' | 'complete';

interface AuthState {
  session: Session | null;
  user: User | null;
  loading: boolean;
  bypass: boolean;
  profileStatus: ProfileStatus;
  /** Re-check users-row status, e.g. after FinishSignup submits. */
  refreshProfile: () => void;
}

const AuthContext = createContext<AuthState>({
  session: null,
  user: null,
  loading: true,
  bypass: false,
  profileStatus: 'loading',
  refreshProfile: () => {},
});

const DEMO_COACH_ID = 'a0000000-0000-0000-0000-000000000020';
const DEMO_USER: User = {
  id: DEMO_COACH_ID,
  email: 'demo@4cm.local',
  app_metadata: {},
  user_metadata: {},
  aud: 'authenticated',
  created_at: new Date().toISOString(),
} as User;

// VITE_AUTH_BYPASS is only honoured in dev builds. In production the flag is
// ignored and a console error is emitted if someone tries to ship it.
const BYPASS = import.meta.env.DEV && import.meta.env.VITE_AUTH_BYPASS === 'true';

if (import.meta.env.PROD && import.meta.env.VITE_AUTH_BYPASS === 'true') {
  // eslint-disable-next-line no-console
  console.error(
    '[web2] VITE_AUTH_BYPASS is set in a production build — ignored. ' +
    'Remove it from the deploy environment.',
  );
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(BYPASS ? DEMO_USER : null);
  const [loading, setLoading] = useState(!BYPASS);
  // BYPASS demo coach is seeded — treat the profile as complete so
  // the rest of the app renders. Real users start at 'loading' and
  // resolve to 'missing' or 'complete' once we check public.users.
  const [profileStatus, setProfileStatus] = useState<ProfileStatus>(
    BYPASS ? 'complete' : 'loading',
  );
  const [profileTick, setProfileTick] = useState(0);

  const refreshProfile = useCallback(() => {
    setProfileTick((t) => t + 1);
  }, []);

  useEffect(() => {
    if (BYPASS) {
      void ensureCoachUser({ id: DEMO_COACH_ID, email: 'demo@4cm.local' });
      return;
    }

    supabase.auth.getSession().then(({ data: { session: s } }) => {
      setSession(s);
      setUser(s?.user ?? null);
      setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_e, s) => {
      setSession(s);
      setUser(s?.user ?? null);
      setLoading(false);
      // Force a profile re-check on every auth-state change. A new
      // sign-in starts as 'loading' until the public.users probe
      // resolves.
      setProfileStatus('loading');
    });

    return () => subscription.unsubscribe();
  }, []);

  // Check whether the signed-in user has a public.users row with DoB.
  // Without DoB the v1 age gate can't run — we treat the row as
  // missing and show finish-signup. This effect re-runs on every
  // auth change + every refreshProfile() call.
  useEffect(() => {
    if (BYPASS) return;
    if (!user?.id) {
      setProfileStatus('loading');
      return;
    }
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from('users')
        .select('id, dob')
        .eq('id', user.id)
        .maybeSingle();
      if (cancelled) return;
      if (error) {
        // Treat fetch failure conservatively — leave the gate up so
        // the user retries. Don't accidentally let an under-13 row
        // get created because the lookup briefly failed.
        setProfileStatus('missing');
        return;
      }
      setProfileStatus(data && data.dob ? 'complete' : 'missing');
    })();
    return () => { cancelled = true; };
  }, [user, profileTick]);

  return (
    <AuthContext.Provider
      value={{
        session,
        user,
        loading,
        bypass: BYPASS,
        profileStatus,
        refreshProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
